import { CurrencyPipe, DatePipe, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, OnDestroy, OnInit, output, ViewEncapsulation } from '@angular/core';
import { AppProjectChangeNotificationService } from '@app/common/services/app-signal.service';
import { PlexEnvironment } from '@app/env';
import { TabHeaderComponent } from '@app/shared/components/tab-header/tab-header.component';
import { ButtonLoadingDirective } from '@app/shared/directives/button-loading/button-loading.directive';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { Project, ProjectEstimate } from '@app/shared/models';
import { IProjectEstimateChange } from '@app/shared/models/project/project-estimate-change';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { GridDataResult, GridModule, RowArgs, RowClassArgs, SelectableSettings, SelectionEvent } from '@progress/kendo-angular-grid';
import { SVGIconComponent } from '@progress/kendo-angular-icons';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { TabStripModule } from '@progress/kendo-angular-layout';
import { process, SortDescriptor, State } from '@progress/kendo-data-query';
import { ErrorService } from 'app/common/services/error.service';
import { ProjectEstimateStatus, ProjectEstimateStatusDescription, ProjectStatusId, ProjectTypeId } from 'app/shared/data/project-enums';
import { Subscription } from 'rxjs';
import { RecordStatus } from '../../enums/record-status';
import { EstimateState } from '../../models/estimate-state';
import { EstimateTab } from '../../models/estimate-tab';
import { ProjectEstimateComparisonData, ProjectEstimateComparisonTableData } from '../../models/project-estimate-comparison';
import { AfeDataSharingService } from '../../services/afe-data-sharing.service';
import { EstimateStateService } from '../../services/estimate-state.service';
import { EstimateTabService } from '../../services/estimate-tab.service';
import { ProjectEstimateService } from '../../services/project-estimate.service';
import { EstimateNotesFormComponent } from './estimate-notes-form/estimate-notes-form.component';
import { ProjectEstimateNote } from './model';

interface SelectedGridRow {
  EstimateId: number;
  EstimateVersion?: number | null;
  SelectedGridRow: number;
}

interface ProjectEstimateDisplay extends ProjectEstimate {
  isSelected: boolean;
  hasWorkflow: boolean;
}

@Component({
  selector: 'app-project-estimate-summary',
  templateUrl: './project-estimate-summary.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./project-estimate-summary.component.scss'],
  imports: [
    ButtonDirective,
    ButtonLoadingDirective,
    CurrencyPipe,
    GridModule,
    DatePipe,
    DialogModule,
    EstimateNotesFormComponent,
    LoaderComponent,
    NgStyle,
    SVGIconComponent,
    TabHeaderComponent,
    TabStripModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    EstimateStateService,
  ],
})
export class ProjectEstimateSummaryComponent implements OnInit, OnDestroy {
  private estimateTabService = inject(EstimateTabService);
  private projectEstimateService = inject(ProjectEstimateService);
  private estimateStateService = inject(EstimateStateService);
  private errorService = inject(ErrorService);
  private cdr = inject(ChangeDetectorRef);
  private projectChangeNotify = inject(AppProjectChangeNotificationService);
  private afeDataSharingService = inject(AfeDataSharingService);
  public environment = inject(PlexEnvironment);

  public icons = kendoIcons;

  public dProjectEstimate = Array<ProjectEstimateDisplay>();
  public disableCompare: boolean = true;
  public disableCopy: boolean = true;
  public disableCreateNew: boolean = false;
  public disableOpen: boolean = true;
  public dialogEstimateCopyOpen: boolean = false;
  public displayEstimateCopyDialog: boolean = false;
  public displayDraftDeleteDialog: boolean = false;
  public displayCompareDialog: boolean = false;
  public isCreatingDraft: boolean = false;

  public gridView: GridDataResult;
  public estimateDataView: GridDataResult;
  public sortData: SortDescriptor[] = [];
  public selectedRow: SelectedGridRow;
  public selectedRows: SelectedGridRow[] = [];
  public selectedItems: RowArgs[] = [];
  public selectedKeys: any = [];
  public estimateTab: EstimateTab;
  public estimateTabs: EstimateTab[] = new Array<EstimateTab>();
  public headerTitle: string;
  public hasDraftEstimate: boolean;
  public draftEstimateId: number;
  public recordTypeAfe: string = '&Rectype=AFE&Prjrev=';
  public recordTypePcr: string = '&Rectype=PCR&Prjrev=';
  public urlAfeRetrive: string;
  public urlAfeRevRetrive: string;
  public urlExecuteAfe: string;
  public urlPcrRetrieve: string;
  public projectHasAfeRecord: boolean;
  public projectHasAfeRevRecord: boolean;
  public projectHasPcrRecord: boolean;
  public projectAfeRecordStatus: string;
  public projectAfeRevRecordStatus: string;
  public projectPcrRecordStatus: string;
  public projectHasActiveAfe: boolean;
  public projectHasApprovedAfe: boolean;
  public projectHasActiveAfeRevision: boolean;
  public projectHasApprovedAfeRevision: boolean;
  public projectHasActivePcr: boolean;
  public projectHasApprovedPcr: boolean;
  public estimateState: EstimateState;
  public afeBudget: number;
  public convertedProjectId: string;
  public selectableSettings: SelectableSettings;
  public copying: boolean = false;
  public showNotes: boolean;
  public editDataItem: ProjectEstimateNote;
  public estimateCompareData: GridDataResult;
  public loading: boolean = false;
  public EstimateOne: string = '';
  public EstimateTwo: string = '';
  public EstimateOneData: ProjectEstimateComparisonData;
  public EstimateTwoData: ProjectEstimateComparisonData;
  public EstimateParameterData: ProjectEstimateComparisonTableData[] = [];
  public totalEstimateOne: number;
  public totalEstimateTwo: number;
  public totalVariance: number;
  public aggregates: any[] = [
    { field: 'Estimate1Total', aggregate: 'sum' },
    { field: 'Estimate2Total', aggregate: 'sum' },
    { field: 'Variance', aggregate: 'sum' }
  ];
  public combinedData: Array<any> = [];
  public state: State = {
    sort: [],
    skip: 0,
    group: [{ field: 'Phase', aggregates: this.aggregates }, { field: 'Step', aggregates: this.aggregates }]
  };
  public showEstimateCompareButton: boolean = false;
  public showForecastOverride: boolean = false;
  public isAfeSyncInProgress: boolean = false;
  public afeNumber: string;
  public afeSubscription = new Subscription();

  readonly iProjectId = input<number>(undefined);
  readonly iProjectTypeId = input<number>(undefined);
  readonly Project = input<Project>(undefined);
  readonly iProjectStatusId = input<number>(undefined);
  readonly projectEstimateState = output<EstimateState>();
  readonly closeDraftTab = output<any>();

  constructor() {
    this.showEstimateCompareButton = this.environment.showEstimateCompareButton;

    this.selectableSettings = {
      mode: 'multiple'
    };
  }

  private fixProjectId(id: number): string {
    let stringId: string = id.toString();
    let fixedId: string;
    if (stringId.length < 6) {
      fixedId = '0'.concat(stringId);
    } else {
      fixedId = stringId;
    }
    return fixedId;
  }

  private getEstimateIdFromDraft(): ProjectEstimateDisplay {
    return this.dProjectEstimate.find(x => x.ProjectEstimateStatusId === 1);
  }

  public disableAllEdits(): boolean {
    const ProjectValue = this.Project();
    return (ProjectValue.ProjectStatusId === ProjectStatusId.Closed || ProjectValue.ProjectStatusId === ProjectStatusId.Cancelled);
  }

  async ngOnInit() {
    this.getConfigSettings();

    this.headerTitle = ' ';
    this.hasDraftEstimate = false;

    this.selectedRow = {
      EstimateId: null,
      EstimateVersion: 9999,
      SelectedGridRow: -1
    };

    this.selectedRows = [];
    this.convertedProjectId = this.fixProjectId(this.iProjectId());

    //   TODO: Currently this is causing an error for some weird reason
    this.projectChangeNotify.NotifyEstimateChange.subscribe(async (change) => {
      if (!change || change.ProjectId !== this.iProjectId()) return;
      await this.updateOneEstimate(change);
    });
    this.afeSubscription = this.afeDataSharingService.afeNumber.subscribe((value) => {
      this.afeNumber = value;
    })
  }

  async ngOnDestroy() {
    this.afeSubscription.unsubscribe();
  }

  async updateOneEstimate(change: IProjectEstimateChange) {
    let toChange = this.dProjectEstimate.find(x => x.ProjectEstimateId == change.ProjectEstimateId);

    if (!toChange) {
      await this.getEstimates(this.iProjectId());
      this.errorService.display('Estimate has Refreshed.', null, false);
      //TODO: Lock all edits, force user to refresh
      return; //TODO: consider ADDING a new estimate... maybe created by another user, that would be cool
    }

    let anyChanges = false;
    let changedFields: string[] = [];
    change.Changes.forEach(one => {
      let fn = one.fieldName;
      if (fn in toChange) {
        let estimateValue = toChange[fn];
        if ((estimateValue || '').toString() != (one.newValue || '').toString()) {
          anyChanges = true;
          changedFields.push(one.fieldName);
        }
      }
    })
    if (anyChanges) {
      //refresh this estimate. for now, just re-get them all
      await this.getEstimates(this.iProjectId());
      this.errorService
        .display(`Project ${change.ProjectId} Estimate # ${change.EstimateVersion} has been updated from the server by: ${change.UpdatedBy}. Changed Fields: ${changedFields.join(',')}`, null, false);
    }
  }

  public getConfigSettings(): void {
    this.projectEstimateService.getConfigSettings()
      .then(async payload => {
        this.urlExecuteAfe = payload.ExecuteAFESettings.ExecuteUrl;
        await this.refreshData();
      });
  }

  public async getEstimates(projectId?: number) {
    this.hasDraftEstimate = false;
    this.projectHasActiveAfe = false;
    this.projectHasApprovedAfe = false;
    this.projectHasActiveAfeRevision = false;
    this.projectHasApprovedAfeRevision = false;
    this.projectHasActivePcr = false;
    this.projectHasApprovedPcr = false;
    const projectIdToUse = projectId ?? this.iProjectId();

    let payload = await this.projectEstimateService.getEstimatesByProjectId(projectIdToUse);

    this.dProjectEstimate = payload.map(estimate => this.ProcessEstimate(estimate));

    this.setEstimateGrid();
    this.setEstimateState();
  }

  private setEstimateState() {
    let state: EstimateState = {
      HasAfeRecord: this.projectHasAfeRecord,
      HasAfeRevRecord: this.projectHasAfeRevRecord,
      HasPcrRecord: this.projectHasPcrRecord,
      AfeRecordStatus: this.projectAfeRecordStatus,
      AfeRevRecordStatus: this.projectAfeRecordStatus,
      PcrRecordStatus: this.projectPcrRecordStatus,
      ProjectId: this.iProjectId(),
      HasActiveAfe: this.projectHasActiveAfe,
      HasApprovedAfe: this.projectHasApprovedAfe,
      HasActiveAfeRevision: this.projectHasActiveAfeRevision,
      HasApprovedAfeRevision: this.projectHasApprovedAfeRevision,
      HasActivePcr: this.projectHasActivePcr,
      HasApprovedPcr: this.projectHasApprovedPcr
    };

    this.estimateStateService.estimateState(state);

    this.projectEstimateState.emit(state);
    this.estimateTabService.updateTabs(this.dProjectEstimate);

    if (this.getEstimateIdFromDraft() !== undefined) {
      this.hasDraftEstimate = true;
    }
  }

  private setEstimateGrid() {
    this.gridView = {
      data: this.dProjectEstimate.map(x => x),
      total: this.dProjectEstimate.length
    };
  }

  private ProcessEstimate(estimate: ProjectEstimate): ProjectEstimateDisplay {
    switch (estimate.ProjectAttachmentTypeId) {
      case 1:
        this.projectHasAfeRecord = true;
        this.projectAfeRecordStatus = estimate.RecordStatus;
        // check for final decision on the Afe. FAPP: Fully Approved and IREJ: Internally Rejected
        if ((this.projectAfeRecordStatus === RecordStatus.Approved)
          || (this.projectAfeRecordStatus === RecordStatus.FAPP)
          || (this.projectAfeRecordStatus === RecordStatus.IREJ)
          || (this.projectAfeRecordStatus == RecordStatus.IAPP)
          || (this.projectAfeRecordStatus == RecordStatus.REJT)
        ) {
          this.projectHasApprovedAfe = true;
        }
        // Check different flag for an active AFE as it moves through different stages in Execute environment
        if ((this.projectAfeRecordStatus === RecordStatus.Submitted) ||
          (this.projectAfeRecordStatus === RecordStatus.UNREL) ||
          (this.projectAfeRecordStatus === RecordStatus.REL) ||
          (this.projectAfeRecordStatus === RecordStatus.IHLP) ||
          (this.projectAfeRecordStatus === RecordStatus.ROUTED)) {
          this.projectHasActiveAfe = true;
        }
        break;
      case 2:
        this.projectHasPcrRecord = true;
        this.projectPcrRecordStatus = estimate.RecordStatus;
        if (estimate.RecordStatus === RecordStatus.Approved) {
          this.projectHasApprovedPcr = true;
        }
        // store flag for an active PCR (Approved, Pending, Submitted), this is necessary due to the possibility of having multiple
        // AFE record types due to rejected PCRs
        if ((estimate.RecordStatus === RecordStatus.Pending) ||
          (estimate.RecordStatus === RecordStatus.Submitted) ||
          (estimate.RecordStatus === null) ||
          (estimate.RecordStatus === RecordStatus.Partner)) {
          this.projectHasActivePcr = true;
        }
        break;
      case 3:
        this.projectHasAfeRevRecord = true;
        this.projectAfeRevRecordStatus = estimate.RecordStatus;
        if ((estimate.RecordStatus === RecordStatus.Approved)
          || (this.projectAfeRevRecordStatus === RecordStatus.FAPP)
          || (this.projectAfeRevRecordStatus === RecordStatus.IAPP)
          || (this.projectAfeRevRecordStatus === RecordStatus.REJT)
          || (this.projectAfeRevRecordStatus === RecordStatus.IREJ)) {
          this.projectHasApprovedAfeRevision = true;
        }
        // store flag for an active AFE Revision (Approved, Pending, Submitted), this is necessary due to the possibility of having multiple
        // AFE record types due to rejected AFE Revisions
        if ((estimate.RecordStatus === RecordStatus.Submitted) ||
          (estimate.RecordStatus === RecordStatus.UNREL) ||
          (estimate.RecordStatus === RecordStatus.REL) ||
          (estimate.RecordStatus === RecordStatus.IHLP) ||
          (estimate.RecordStatus === RecordStatus.ROUTED)) {
          this.projectHasActiveAfeRevision = true;
        }
        break;
    }

    const hasWorkflow = estimate.Workflow !== null && estimate.Workflow !== '';

    return Object.assign(estimate, { isSelected: false, hasWorkflow });
  }

  public updateEstimateState(): void {
    let state: EstimateState = {
      HasAfeRecord: this.projectHasAfeRecord,
      HasAfeRevRecord: this.projectHasAfeRevRecord,
      HasPcrRecord: this.projectHasPcrRecord,
      AfeRecordStatus: this.projectAfeRecordStatus,
      AfeRevRecordStatus: this.projectAfeRevRecordStatus,
      PcrRecordStatus: this.projectPcrRecordStatus,
      ProjectId: this.iProjectId(),
      HasActiveAfe: this.projectHasActiveAfe,
      HasApprovedAfe: this.projectHasApprovedAfe,
      HasActiveAfeRevision: this.projectHasActiveAfeRevision,
      HasApprovedAfeRevision: this.projectHasApprovedAfeRevision,
      HasActivePcr: this.projectHasActivePcr,
      HasApprovedPcr: this.projectHasApprovedPcr
    };
    this.estimateStateService.estimateState(state);
  }

  public async requestCopyEstimate(): Promise<void> {
    let obj = this.getEstimateIdFromDraft();
    if (obj !== undefined) {
      this.requestDeleteDraft(obj)
        .then(async x => {
          await this.refreshData();
          this.copyEstimate();
        });
    } else {
      this.copyEstimate();
    }
  }

  public async requestDeleteDraft(obj: any) {
    let projectId: number = obj.ProjectId;
    let estimateId: number = obj.ProjectEstimateId;
    await this.projectEstimateService.deleteDraftEstimate(projectId, estimateId);

    this.closeDraftTab.emit(null);

    return new Promise(resolve => setTimeout(resolve, 3000));
  }

  public isDeleting = false;

  public async acceptDeleteDraft(): Promise<any> {
    if (this.selectedItems.length !== 1 || this.anyNonDraftsSelected()) {
      return;
    }

    let selectedProject = this.selectedItems[0].dataItem;

    this.displayDraftDeleteDialog = false;
    this.isDeleting = true;
    await this.requestDeleteDraft(selectedProject);
    await this.refreshData();
    this.isDeleting = false;
  }

  public anyNonDraftsSelected() {
    let result: boolean = false;
    for (let i = 0; i < this.selectedItems.length; i++) {
      if (this.selectedItems[i].dataItem.ProjectEstimateStatus.Description !== ProjectEstimateStatusDescription.Draft) {
        result = true;
      }
      if (this.selectedItems[i].dataItem.ProjectEstimateStatus.Description === ProjectEstimateStatusDescription.Draft && this.selectedItems[i].dataItem.EstimateVersion === 1) {
        result = true;
      }
    }

    return result;
  }

  public isDraftSelected() {
    let result: boolean = false;
    for (let i = 0; i < this.selectedItems.length; i++) {
      if (this.selectedItems[i].dataItem.ProjectEstimateStatus.Description === ProjectEstimateStatusDescription.Draft) {
        result = true;
      }
    }

    return result;
  }

  private async runCopyEstimateToDraft(): Promise<void> {
    let version: number = this.selectedItems[0].dataItem.EstimateVersion;
    await this.projectEstimateService.copyEstimate(this.iProjectId(), version);
    this.projectEstimateService.estimateChanged.next(true);
    this.copying = false;
    await this.refreshData();
  }

  private async createDraftEstimateAndTriggerEngine(): Promise<void> {
    this.isCreatingDraft = true;
    try {
      await this.projectEstimateService.createNewEstimate(this.iProjectId());
      await this.applyLegacyTaxIfNeeded();
      await this.projectEstimateService.startEstimateEngine(this.iProjectId(), this.environment.useNewEngine ? 3 : 1);
      this.projectEstimateService.estimateChanged.next(true);
      await this.refreshData();
    }
    finally {
      this.copying = false;
      this.isCreatingDraft = false;
    }
  }

  public copyEstimate() {
    let version: number = this.selectedItems[0].dataItem.EstimateVersion;
    this.projectEstimateService.copyEstimate(this.iProjectId(), version)
      .then(async x => {
        await this.refreshData();
      });
  }

  public async createDraft(): Promise<void> {
    this.createDraftEstimateAndTriggerEngine();
  }

  
  private getTaxPercent(): number {
    let percent = 0.0;
    switch (this.iProjectTypeId()) {
      case ProjectTypeId.DrillOilGasWell:
        percent = 0.022;
        break;
      case ProjectTypeId.CompleteOilGasWell:
        percent = 0.01;
        break;
      case ProjectTypeId.CompleteStimOAG:
        percent = 0.005;
        break;
      case ProjectTypeId.CompletePostStimOAG:
        percent = 0.022;
        break;
    }
    return percent;
  }
  
  public getMaxVersion(estimates: any[]): number {
    return estimates.reduce((max, p) => p.EstimateVersion > max ? p.EstimateVersion : max, estimates[0].EstimateVersion);
  }

  public sortDataChange(sort: SortDescriptor[]): void {
    this.sortData = sort;
  }

  public onRowClick(event, dataItem: ProjectEstimateDisplay, rowIndex: number) {
    if (event.ctrlKey && this.selectedRows.length < 2 && !this.gridView.data[rowIndex].isSelected) {
      this.selectedRow.EstimateId = dataItem.ProjectEstimateId;
      this.selectedRow.EstimateVersion = dataItem.EstimateVersion;
      this.selectedRow.SelectedGridRow = rowIndex;
      this.selectedRows.push(this.selectedRow);
      this.gridView.data[rowIndex].isSelected = true;
      if (this.selectedRows.length === 2) {
        this.disableCompare = false;
        this.disableCopy = true;
        this.disableOpen = true;
        this.disableCreateNew = true;
      } else {
        this.disableCompare = true;
        if (this.selectedRows.length === 1) {
          this.disableCopy = false;
          this.disableOpen = false;
        } else {
          this.disableCopy = true;
          this.disableOpen = true;
        }
      }
    } else {
      if (this.gridView.data[rowIndex].isSelected) {
        this.clearSelectedRows();
      } else {
        this.clearSelectedRows();
        this.selectedRow.SelectedGridRow = rowIndex;
        this.selectedRow.EstimateId = dataItem.ProjectEstimateId;
        this.selectedRow.EstimateVersion = dataItem.EstimateVersion;
        this.disableOpen = false;
        if (dataItem.ProjectEstimateStatusId === 1) {
          this.disableCopy = true;
        } else {
          this.disableCopy = false;
        }
        this.selectedRows.push(this.selectedRow);
        this.gridView.data[rowIndex].isSelected = true;
      }
      if (this.selectedRows.length === 0) {
        this.disableOpen = true;
      }
    }
  }

  public selectionChange(event: SelectionEvent) {
    event.selectedRows.forEach((row) => {
      this.selectedItems.push(row);
    });
    event.deselectedRows.forEach((row) => {
      this.selectedItems = this.selectedItems.filter(f => f.index !== row.index);
    });
  }

  public clearSelectedRows() {
    this.selectedKeys = [];
    this.selectedItems = [];
  }

  public onRowDblClick(event, dataItem: ProjectEstimateDisplay, rowIndex: number) {
    this.createTab();
  }

  public resetProjectDefaults(): void {
    this.clearSelectedRows();
    this.projectHasAfeRecord = false;
    this.projectHasAfeRevRecord = false;
    this.projectHasPcrRecord = false;
    this.projectAfeRecordStatus = null;
    this.projectAfeRevRecordStatus = null;
    this.projectPcrRecordStatus = null;

    this.disableCompare = true;
    this.disableCopy = true;
    this.disableCreateNew = false;
    this.isCreatingDraft = false;
  }

  public async refreshData() {
    this.resetProjectDefaults();
    await this.getEstimates(this.iProjectId());
  }

  public createTab() {
    if (this.selectedItems.length === 0) {
      return;
    }

    let selectedRow = this.selectedItems[0];
    if (this.disableAllEdits() && selectedRow.dataItem.ProjectEstimateStatusId === 1) {
      alert('Draft Estimate cannot be opened on a Project that is Closed or Cancelled');
      return;
    }

    let x: any = selectedRow.dataItem.EstimateVersion;
    let z: EstimateTab = new EstimateTab(selectedRow.dataItem as ProjectEstimate, `${this.convertedProjectId}-V${x}`, true, false);
    this.estimateTabService.estimateTab(z);
    this.updateEstimateState();
    this.clearSelectedRows();
  }

  public declineCopyEstimate(): void {
    this.displayEstimateCopyDialog = false;
  }

  public acceptCopyEstimate(): void {
    this.displayEstimateCopyDialog = false;
    this.copying = true;
    this.runCopyEstimateToDraft();
  }

  public closeCopyDialog() {
    this.copying = false;
  }

  public handleCopyEstimate(): void {
    if (this.hasDraftEstimate) {
      this.displayEstimateCopyDialog = true;
    } else {
      this.copying = true;
      this.runCopyEstimateToDraft();
    }
  }

  rowCallback(context: RowClassArgs) {
    const isEven = context.index % 2 === 0;
    return {
      even: isEven,
      odd: !isEven
    };
  }

  public editHandler({ dataItem }) {
    this.editDataItem =
    {
      ProjectEstimateId: dataItem.ProjectEstimateId,
      RecordDescription: dataItem.RecordDescription
    };
  }

  public cancelHandler() {
    this.editDataItem = undefined;
  }

  public async saveHandler(note: ProjectEstimateNote) {
    let update = {
      RecordDescription: note.RecordDescription
    };

    await this.projectEstimateService.patchEstimate(note.ProjectEstimateId, update);
    await this.refreshData();
    this.editDataItem = undefined;
  }

  public onStateChange(state: State) {
    if (state && state.group) {
      state.group.map(group => group.aggregates = this.aggregates);
    }
    this.state = state;
    this.estimateCompareData = process(this.combinedData, this.state);
  }

  public compareEstimates(): void {
    this.loading = true;
    this.displayCompareDialog = true;
    let isDraftSelected: boolean = false;
    let projectEstimateId: number = 0;
    let compareProjectEstimateId: number = 0;
    for (let i = 0; i < this.selectedItems.length; i++) {
      if (this.selectedItems[i].dataItem.ProjectEstimateStatus.Description === ProjectEstimateStatusDescription.Draft) {
        isDraftSelected = true;
        projectEstimateId = this.selectedItems[i].dataItem.ProjectEstimateId;
        this.EstimateOne = `Estimate V${this.selectedItems[i].dataItem.EstimateVersion} (${this.selectedItems[i].dataItem.ProjectEstimateStatus.Description})`;
        let elem = i === 0 ? 1 : 0;
        compareProjectEstimateId = this.selectedItems[elem].dataItem.ProjectEstimateId;
        this.EstimateTwo = `Estimate V${this.selectedItems[elem].dataItem.EstimateVersion} (${this.selectedItems[elem].dataItem.ProjectEstimateStatus.Description})`;
      }
    }
    if (!isDraftSelected) {
      projectEstimateId = this.selectedItems[0].dataItem.ProjectEstimateId;
      this.EstimateOne = `Estimate V${this.selectedItems[0].dataItem.EstimateVersion} (${this.selectedItems[0].dataItem.ProjectEstimateStatus.Description})`;
      compareProjectEstimateId = this.selectedItems[1].dataItem.ProjectEstimateId;
      this.EstimateTwo = `Estimate V${this.selectedItems[1].dataItem.EstimateVersion} (${this.selectedItems[1].dataItem.ProjectEstimateStatus.Description})`;
    }
    this.projectEstimateService.getEstimateComparisonByPhaseStep(projectEstimateId, compareProjectEstimateId, isDraftSelected)
      .subscribe(payload => {
        this.combinedData = payload;
        this.estimateCompareData = process(payload, this.state);
        this.totalEstimateOne = payload.map(t => t.Estimate1Total).reduce((v, i) => (v + i));
        this.totalEstimateTwo = payload.map(t => t.Estimate2Total).reduce((v, i) => (v + i));
        this.totalVariance = payload.map(t => t.Variance).reduce((v, i) => (v + i));
        this.loading = false;
        this.cdr.detectChanges();
      });
    this.projectEstimateService.getEstimateComparisonByParameter(projectEstimateId, compareProjectEstimateId)
      .subscribe(payload => {
        this.loading = false;
        this.EstimateOneData = payload.EstimateOne;
        this.EstimateTwoData = payload.EstimateTwo;
        this.MapEstimateComparisonData();
        this.cdr.detectChanges();
      });
  }

  public getGroupedHeader(column, agg) {
    let val = agg.value ? agg.value : 'NA';
    let cp = new CurrencyPipe('en-US');

    return val + ' - Total1: ' + cp.transform(agg.aggregates['Estimate1Total'].sum, 'USD', 'symbol-narrow') +
      ' - Total2: ' + cp.transform(agg.aggregates['Estimate2Total'].sum, 'USD', 'symbol-narrow') +
      ' - Variance: ' + cp.transform(agg.aggregates['Variance'].sum, 'USD', 'symbol-narrow');
  }

  public MapEstimateComparisonData() {
    this.EstimateParameterData = [];
    this.EstimateParameterData.push({ Name: 'Completion Design', Value1: this.EstimateOneData.CompletionsDesign, Value2: this.EstimateTwoData.CompletionsDesign, Format: '' });
    this.EstimateParameterData.push({ Name: 'Drilling Design', Value1: this.EstimateOneData.DrillingDesign, Value2: this.EstimateTwoData.DrillingDesign, Format: '' });
    this.EstimateParameterData.push({ Name: 'Formation', Value1: this.EstimateOneData.Formation, Value2: this.EstimateTwoData.Formation, Format: '' });
    this.EstimateParameterData.push({ Name: 'Lateral Length', Value1: this.EstimateOneData.LateralLength == null ? '' : this.EstimateOneData.LateralLength.toString(), Value2: this.EstimateTwoData.LateralLength == null ? '' : this.EstimateTwoData.LateralLength.toString(), Format: 'number' });
    this.EstimateParameterData.push({ Name: 'Lateral Length Override', Value1: this.EstimateOneData.LateralLengthOverride == null ? '' : this.EstimateOneData.LateralLengthOverride.toString(), Value2: this.EstimateTwoData.LateralLengthOverride == null ? '' : this.EstimateTwoData.LateralLengthOverride.toString(), Format: 'number' });
    this.EstimateParameterData.push({ Name: 'Total Depth', Value1: this.EstimateOneData.TotalDepth == null ? '' : this.EstimateOneData.TotalDepth.toString(), Value2: this.EstimateTwoData.TotalDepth == null ? '' : this.EstimateTwoData.TotalDepth.toString(), Format: 'number' });
    this.EstimateParameterData.push({ Name: 'Total Duration', Value1: this.EstimateOneData.TotalDuration == null ? '' : this.EstimateOneData.TotalDuration.toString(), Value2: this.EstimateTwoData.TotalDuration == null ? '' : this.EstimateTwoData.TotalDuration.toString(), Format: 'number' });
  }

  public closeCompareDialog(): void {
    this.displayCompareDialog = false;
    this.clearSelectedRows();
  }

  private element: Event = null;
  private selectedEstimate: ProjectEstimate = null;

  public onIsForecastOverrideChange(selected: ProjectEstimate, event: Event): void {
    this.element = event;
    this.selectedEstimate = selected;
    this.showForecastOverride = true;
  }

  cancelForecastOverride() {
    let checked: boolean = (this.element.target as HTMLInputElement).checked;
    (this.element.target as HTMLInputElement).checked = !checked;
    this.showForecastOverride = false;
  }

  async acceptForecastOverride() {
    let checked: boolean = (this.element.target as HTMLInputElement).checked;
    const updates = [];

    updates.push({ ProjectEstimateId: this.selectedEstimate.ProjectEstimateId, IsForecastOverride: checked });
    this.selectedEstimate.IsForecastOverride = checked;

    //if checked == true, need to set other values to false
    if (checked) {
      this.dProjectEstimate.forEach(est => {
        if (est.ProjectEstimateId != this.selectedEstimate.ProjectEstimateId && est.IsForecastOverride) {
          updates.push({ ProjectEstimateId: est.ProjectEstimateId, IsForecastOverride: false });
          est.IsForecastOverride = false;
        }
      })
    }
    this.showForecastOverride = false;
    await this.projectEstimateService.updateForecastOverrides(updates);
  }
  public updateAfeStatus() {
    this.isAfeSyncInProgress = true;
    this.projectEstimateService.UpdateAfeStatus(this.iProjectId().toString(), this.afeNumber)
      .then(() => {
        this.getEstimates(this.iProjectId())
      }).finally(() => {
        this.isAfeSyncInProgress = false;
      });
  }

  private async applyLegacyTaxIfNeeded(): Promise<void> {
    const legacyTax = this.getTaxPercent();
    if (legacyTax === 0) return;

    const estimates = await this.projectEstimateService.getEstimatesByProjectId(this.iProjectId());
    const draft = estimates.find(e => e.ProjectEstimateStatusId === ProjectEstimateStatus.Draft);
    if (draft && !draft.Tax) {
      await this.projectEstimateService.patchEstimate(draft.ProjectEstimateId, { Tax: legacyTax });
    }
  }
}